import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";
import PageGuide from "@/components/PageGuide";
import { usePageGuide } from "@/hooks/usePageGuide";
import { getGuideForPath } from "@/lib/page-guides";
import { HelpCircle, AlertTriangle, CreditCard } from "lucide-react";
import PageExplainer from "@/components/PageExplainer";
import WhatsAppButton from "@/components/WhatsAppButton";
import RealtimeStatusBadge from "@/components/RealtimeStatusBadge";
import MobileBottomNav from "@/components/MobileBottomNav";
import { Button } from "@/components/ui/button";
import { useSubscription } from "@/hooks/useSubscription";
import NurseryProfileCompletionBanner from "@/components/NurseryProfileCompletionBanner";
import { useGlobalRealtimeSync } from "@/hooks/useGlobalRealtimeSync";

const DashboardLayout = () => {
  const { profile, roles } = useAuth();
  useGlobalRealtimeSync();
  const location = useLocation();
  const navigate = useNavigate();
  const steps = getGuideForPath(location.pathname);
  const guide = usePageGuide(steps);
  const { isExpired, isSuspended, isTrial, daysRemaining, plan } = useSubscription();

  const isParent = roles.includes("parent") && !roles.includes("admin");
  const isSuperAdmin = roles.includes("super_admin");
  const showBanner = !isParent && !isSuperAdmin && (isExpired || isSuspended || (isTrial && daysRemaining !== null && daysRemaining <= 5));

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <div data-guide="sidebar">
          <AppSidebar />
        </div>
        <div className="flex-1 flex flex-col">
          {/* Subscription Warning Banner */}
          {showBanner && (
            <div className={`px-4 py-2.5 flex items-center justify-between gap-3 text-sm ${
              isExpired ? "bg-destructive/10 text-destructive border-b border-destructive/20" :
              isSuspended ? "bg-orange-500/10 text-orange-700 dark:text-orange-400 border-b border-orange-500/20" :
              "bg-blue-500/10 text-blue-700 dark:text-blue-400 border-b border-blue-500/20"
            }`}>
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0" />
                <span>
                  {isExpired
                    ? "اشتراكك منتهي — بعض الميزات محدودة. يرجى تجديد الاشتراك."
                    : isSuspended
                      ? "اشتراكك معلّق — يرجى التواصل مع الدعم أو تجديد الاشتراك."
                      : `فترة التجربة المجانية تنتهي خلال ${daysRemaining} ${daysRemaining === 1 ? "يوم" : "أيام"}`}
                </span>
              </div>
              <Button
                size="sm"
                variant={isExpired ? "destructive" : "outline"}
                className="shrink-0 gap-1.5"
                onClick={() => navigate("/dashboard/subscription")}
              >
                <CreditCard className="w-3.5 h-3.5" />
                {isExpired ? "تجديد الاشتراك" : "ترقية الخطة"}
              </Button>
            </div>
          )}

          <header data-guide="header" className="h-14 flex items-center border-b bg-card px-3 sm:px-4 gap-2 sm:gap-3 sticky top-0 z-30">
            <SidebarTrigger />
            <span className="text-sm text-muted-foreground flex-1 truncate">
              {profile?.full_name || "مرحباً"}
            </span>
            <div className="hidden sm:block"><PageExplainer /></div>
            <div className="hidden sm:block"><RealtimeStatusBadge /></div>
            <Button
              variant="ghost"
              size="icon"
              onClick={guide.start}
              className="h-8 w-8 text-muted-foreground hover:text-primary hidden sm:inline-flex"
              title="دليل الصفحة"
            >
              <HelpCircle className="w-5 h-5" />
            </Button>
            <div data-guide="notifications">
              <NotificationBell />
            </div>
          </header>
          <main data-guide="main-content" className="app-main flex-1 p-4 sm:p-6 overflow-auto has-bottom-nav md:!pb-6">
            <NurseryProfileCompletionBanner />
            <Outlet />
          </main>
        </div>
      </div>

      <MobileBottomNav />
      <WhatsAppButton />
      <PageGuide
        isActive={guide.isActive}
        step={guide.step}
        currentStep={guide.currentStep}
        totalSteps={guide.totalSteps}
        onNext={guide.next}
        onPrev={guide.prev}
        onClose={guide.stop}
      />
    </SidebarProvider>
  );
};

export default DashboardLayout;
